import {
  AlertDialog,
  AlertDialogTrigger,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
  TONE_DOT_CLASS,
  cn,
} from '@vivicta/ui';
import { useGitHubConnection } from '@vivicta/github-connection';
import { LogOut, PanelLeft, Settings as SettingsIcon } from 'lucide-react';
import { Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { SignOutConfirmDialog } from '../settings/SignOutConfirmDialog';
import { initialsOf, useUserIdentity } from '../settings/useUserIdentity';
import { CommandPalette } from './CommandPalette';
import { GitHubStatusChip } from './GitHubStatusChip';
import { NotificationsMenu } from './NotificationsMenu';
import { type BreadcrumbNode, useBreadcrumbs } from './useBreadcrumbs';

const MOBILE_VISIBLE_LEVELS = 3;

const BreadcrumbCrumb = ({
  crumb,
  isLast,
}: {
  crumb: BreadcrumbNode;
  isLast: boolean;
}) => {
  if (isLast) {
    return (
      <BreadcrumbPage className="max-w-[14rem] truncate">
        {crumb.label}
      </BreadcrumbPage>
    );
  }
  if (crumb.to) {
    return (
      <BreadcrumbLink asChild className="max-w-[10rem] truncate">
        <Link to={crumb.to}>{crumb.label}</Link>
      </BreadcrumbLink>
    );
  }
  return (
    <BreadcrumbLink asChild className="max-w-[10rem] truncate">
      <button type="button" onClick={crumb.onClick}>
        {crumb.label}
      </button>
    </BreadcrumbLink>
  );
};

const BreadcrumbNodes = ({
  nodes,
  leadingEllipsis,
}: {
  nodes: BreadcrumbNode[];
  leadingEllipsis: boolean;
}) => (
  <BreadcrumbList className="flex-nowrap">
    {leadingEllipsis && (
      <>
        <BreadcrumbItem>
          <BreadcrumbEllipsis />
        </BreadcrumbItem>
        <BreadcrumbSeparator />
      </>
    )}
    {nodes.map((crumb, index) => {
      const isLast = index === nodes.length - 1;
      return (
        <Fragment key={`${crumb.label}-${index}`}>
          <BreadcrumbItem>
            <BreadcrumbCrumb crumb={crumb} isLast={isLast} />
          </BreadcrumbItem>
          {!isLast && <BreadcrumbSeparator />}
        </Fragment>
      );
    })}
  </BreadcrumbList>
);

/**
 * Renders both the full trail and a `…` + last-3 trail, then lets a Tailwind
 * breakpoint pick one — simpler than juggling per-item `hidden`/`sm:inline`
 * classes across a single shared list. `useBreadcrumbs()` already resolves
 * every route to at least one level (T2.4).
 */
const Breadcrumbs = () => {
  const crumbs = useBreadcrumbs();
  const overflowCount = Math.max(crumbs.length - MOBILE_VISIBLE_LEVELS, 0);
  const collapsed = overflowCount > 0 ? crumbs.slice(overflowCount) : crumbs;

  return (
    <Breadcrumb className="min-w-0 flex-1">
      <div className="sm:hidden">
        <BreadcrumbNodes
          nodes={collapsed}
          leadingEllipsis={overflowCount > 0}
        />
      </div>
      <div className="hidden sm:block">
        <BreadcrumbNodes nodes={crumbs} leadingEllipsis={false} />
      </div>
    </Breadcrumb>
  );
};

/**
 * Bar above the routed content. Rendered as a sibling of `<nav>` inside
 * `VivictaSidebar`'s `SidebarContent`, which places both in whatever slot
 * `NavContentBlueprint` owns — not necessarily in-flow above the routed
 * content column. `position: fixed`, matching how the fixed sidebar `<nav>`
 * in this same file solves the identical problem, rather than `sticky`
 * (which only pins within its own document-flow position).
 * `lg:left-[--vivicta-sidebar-width]` starts the bar after the fixed sidebar
 * instead of on top of it, and at the window edge once the sidebar is
 * collapsed (`useSidebarCollapsed`); `pl-14`
 * (removed again at `lg` via `lg:pl-3`) clears the mobile menu button fixed
 * at `left-3 top-3` (see `VivictaSidebar.tsx`). `theme.ts`'s
 * `BackstageSidebarPage` override reserves an equal `padding-top` on the
 * content column, the same way it already reserves `padding-left` for the
 * sidebar.
 *
 * The breadcrumb bar (T2.4) resolves the current route via `useBreadcrumbs()`;
 * search is a single `CommandPalette` trigger (⌘K/Ctrl+K or `/`) covering
 * pages, templates, actions and live full-text results alike.
 */
export type SidebarToggle = {
  collapsed: boolean;
  toggle: () => void;
  /** `id` of the sidebar element the toggle shows and hides. */
  controls: string;
};

export const PortalTopBar = ({ sidebar }: { sidebar?: SidebarToggle }) => {
  const navigate = useNavigate();
  const { loading, displayName, email, picture } = useUserIdentity();
  const github = useGitHubConnection();

  return (
    <header className="fixed inset-x-0 top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-background/80 pl-14 pr-3 backdrop-blur lg:left-[--vivicta-sidebar-width] lg:pl-3">
      {sidebar && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={sidebar.collapsed ? 'Show sidebar' : 'Hide sidebar'}
          aria-controls={sidebar.controls}
          aria-expanded={!sidebar.collapsed}
          className="hidden shrink-0 lg:inline-flex"
          onClick={sidebar.toggle}
        >
          <PanelLeft aria-hidden="true" className="size-4" />
        </Button>
      )}
      <Breadcrumbs />

      <CommandPalette />

      <GitHubStatusChip connection={github} />

      <NotificationsMenu />

      {loading ? (
        // Same footprint as the size="icon" account button around its size-8 avatar.
        <div
          aria-hidden="true"
          className="flex size-9 shrink-0 items-center justify-center"
        >
          <Skeleton className="size-8 rounded-full" />
        </div>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Account menu"
              className="rounded-full"
            >
              <Avatar className="size-8 border border-border">
                {picture && <AvatarImage src={picture} alt="" />}
                <AvatarFallback className="text-xs font-semibold">
                  {initialsOf(displayName)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex flex-col">
              <span className="truncate font-medium">{displayName}</span>
              {email && (
                <span className="truncate text-xs font-normal text-muted-foreground">
                  {email}
                </span>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/settings')}>
              <SettingsIcon aria-hidden="true" className="mr-2 size-4" />
              Settings
            </DropdownMenuItem>
            {github.state === 'none' && (
              <DropdownMenuItem
                disabled={github.connecting}
                onSelect={() => void github.connect()}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'ml-1 mr-3 size-2 shrink-0 rounded-full',
                    TONE_DOT_CLASS.warn,
                  )}
                />
                Connect GitHub
              </DropdownMenuItem>
            )}
            {github.state === 'connected' && (
              <DropdownMenuLabel className="flex items-center text-xs font-normal text-muted-foreground">
                <span
                  aria-hidden="true"
                  className={cn(
                    'ml-1 mr-3 size-2 shrink-0 rounded-full',
                    TONE_DOT_CLASS.ok,
                  )}
                />
                GitHub connected
              </DropdownMenuLabel>
            )}
            <DropdownMenuSeparator />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                {/* preventDefault keeps the menu mounted long enough for the
                    alert dialog to take over focus (Radix nested-trigger pattern). */}
                <DropdownMenuItem
                  onSelect={event => event.preventDefault()}
                  className="text-destructive-strong focus:text-destructive-strong"
                >
                  <LogOut aria-hidden="true" className="mr-2 size-4" />
                  Sign out
                </DropdownMenuItem>
              </AlertDialogTrigger>
              <SignOutConfirmDialog />
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </header>
  );
};
